import { Cancel, Check, Clear } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type BoQMarkErrorDialogResult =
    | {
          action: 'confirm';
          reason: string;
      }
    | {
          action: 'cancel';
      };

export interface BoQMarkErrorDialogProps {
    title: string;
    description: string;
    initialReason: string;
    open: boolean;
    reasonCatalog?: readonly (readonly [string, string])[];
    onClose: (result: BoQMarkErrorDialogResult) => void | Promise<void>;
}

export default function BoQMarkErrorDialog({
    title,
    description,
    initialReason,
    open,
    reasonCatalog,
    onClose,
}: BoQMarkErrorDialogProps) {
    const { t } = useTranslation();
    const [reason, setReason] = useState(initialReason);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setReason(initialReason);
    }, [initialReason]);

    const handleClose = async (result: BoQMarkErrorDialogResult) => {
        setLoading(true);
        await onClose(result);
        setLoading(false);
    };

    return (
        <Dialog open={open} onClose={() => handleClose({ action: 'cancel' })}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Typography variant="body2">{description}</Typography>

                {reasonCatalog ? (
                    <TextField
                        select
                        label={t('Reason')}
                        sx={{ mt: 2 }}
                        fullWidth
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton onClick={() => setReason('')} edge="start">
                                            <Clear />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    >
                        {reasonCatalog.map(([key, reason]) => (
                            <MenuItem key={key} value={t(reason)}>
                                {t(reason)}
                            </MenuItem>
                        ))}
                    </TextField>
                ) : (
                    <TextField
                        label={t('Reason')}
                        sx={{ mt: 2 }}
                        fullWidth
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setReason('')} edge="end">
                                            <Clear />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                )}

                <Stack sx={{ mt: 2 }} direction="row" spacing={1}>
                    <Button
                        variant="contained"
                        startIcon={<Check />}
                        onClick={() => handleClose({ action: 'confirm', reason })}
                        loading={loading}
                    >
                        {t('Apply')}
                    </Button>
                    <Button
                        variant="text"
                        startIcon={<Cancel />}
                        onClick={() => handleClose({ action: 'cancel' })}
                        loading={loading}
                    >
                        {t('Cancel')}
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    );
}
